import type { TFunction } from "i18next";

import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  AssetGroup,
  CreateProgramApiOptions,
  CreateProgramInput,
  CreateProgramOutcome,
  CreateProgramStepUp,
  LookupResult,
  PartnerSpec,
  PendingCreateResume,
  SubIB,
  SymbolOverride,
} from "../data";
import type { OverrideParty } from "../RebateSplitOverride";
import type { StepUpPhase } from "../StepUpFlow";
import type { WizardStepId } from "./useCreateProgramWizard";

import { Button as ButtonV2 } from "../../../Button";
import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { CrossIcon } from "../../../Icons";
import { ResponsiveModal } from "../../../ResponsiveModal";
import {
  OverrideEntry,
  PARTY_COLOR_CLASSES,
  RebateSplitOverride,
} from "../RebateSplitOverride";
import { DiscardConfirmOverlay } from "./DiscardConfirmOverlay";
import { LookupStep } from "./steps/LookupStep";
import { RelationshipStep } from "./steps/RelationshipStep";
import { ReviewStep } from "./steps/ReviewStep";
import { SplitStep } from "./steps/SplitStep";
import { SuccessStep } from "./steps/SuccessStep";
import { VerifyStep } from "./steps/VerifyStep";
import {
  type CreateProgramWizardResult,
  useCreateProgramWizard,
} from "./useCreateProgramWizard";
import { WizardFooter } from "./WizardFooter";

const getStepNumber = (
  step: WizardStepId,
  partnerCount: number,
  relationshipIndex: number,
): number => {
  switch (step) {
    case "split":
      return 1;
    case "relationship":
      return relationshipIndex + 2;
    case "review":
      return partnerCount + 2;
    case "verify":
      return partnerCount + 3;
    default:
      return 0;
  }
};

const getStepTitle = (
  step: WizardStepId,
  t: TFunction<"translation", undefined>,
  // Set only when the relationship step repeats — see the call site.
  partnerName?: string,
): string => {
  switch (step) {
    case "lookup":
      return t("subIBReport_lookupTitle", "Find Sub IB by email");
    case "relationship":
      return partnerName
        ? t(
            "subIBReport_relationshipTitleNamed",
            "Privacy settings · {{name}}",
            {
              name: partnerName,
            },
          )
        : t("subIBReport_relationshipTitle", "Privacy settings");
    case "split":
      return t("subIBReport_splitTitle", "Set default rebate split");
    case "review":
      return t("subIBReport_reviewTitle", "Review and confirm");
    case "verify":
      return t("subIBReport_stepUpTitle", "Verify it's you");
    case "success":
      return t("subIBReport_successTitle", "Rebate program created");
  }
};

type CreateProgramWizardProps = {
  open: boolean;
  presetSub: SubIB | null;
  /** Injected Sub IB email lookup; defaults to the mock inside LookupStep. */
  lookupSubIb?: (email: string) => Promise<LookupResult>;
  onClose: () => void;
  /** Called when the create call reports the Master is at their program cap. */
  onCapped: () => void;
  onCreated: (
    result: CreateProgramWizardResult,
    partnerSpecs: PartnerSpec[],
  ) => void;
  onViewProgram: (hoidId: string) => void;
  /** Relationship step's Comment field is Phase-2 (part-4039-master-sub-ib-phase-2) — hidden while the flag is off. */
  showRelationshipComment?: boolean;
  /** masterSubIbPhase2: the Pending tag on a pending-KYC Sub IB, the detailed lookup error copy, and the Search lock. */
  phase2Enabled?: boolean;
  showStepUp?: boolean;
  simulateStepUpFailure?: boolean;
  /** Live catalogue; null falls back to the mock in `catalogue.ts`. */
  assets?: AssetGroup[] | null;
  /** Injected create mutation; defaults to the mock submit when omitted. */
  createProgram?: (
    input: CreateProgramInput,
    options?: CreateProgramApiOptions,
  ) => Promise<CreateProgramOutcome>;
  createStepUp?: CreateProgramStepUp;
  resume?: PendingCreateResume | null;
  /** Opens the no-rebate-account modal when the API reports one on submit. */
  onNoRebateAccount?: () => void;
  /**
   * The Master IB's own rebate account, shown read-only on Review. Null (the
   * default) shows Review's own placeholder — the caller doesn't have to
   * supply one outside `SharedRebateProgramsView`.
   */
  masterRebateAccount?: string | null;
};

export const CreateProgramWizard = ({
  open,
  presetSub,
  lookupSubIb,
  onClose,
  onCapped,
  onCreated,
  onViewProgram,
  showRelationshipComment,
  phase2Enabled,
  showStepUp = false,
  simulateStepUpFailure = false,
  assets,
  createProgram,
  createStepUp,
  resume = null,
  onNoRebateAccount,
  masterRebateAccount = null,
}: CreateProgramWizardProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // On mobile, BottomSheet (ResponsiveModal's own mobile branch) would lock
  // scroll itself, but our own full-screen mobile shell below replaces that
  // branch entirely, so it needs the same lock. On desktop, ResponsiveModal's
  // plain-modal branch has no lock of its own either, so this covers both.
  useBodyScrollLock(open);

  // Rebate split overrides live here rather than in `useCreateProgramWizard`
  // because they are not a wizard *step* — the sub-page swaps out the current
  // step's content and returns to it, so the step machine never moves.
  //
  // Declared above the wizard hook so they can be handed to it: the submit needs
  // them, and before that they were configured, shown on Review, and then
  // silently dropped.
  // Seeded from the saved request when the wizard is restored after an Auth0
  // redirect (resume reopens on Review); empty otherwise.
  const [symbolOverrides, setSymbolOverrides] = useState<SymbolOverride[]>(
    () => (showStepUp && resume ? resume.overrides : []),
  );
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideDirty, setOverrideDirty] = useState(false);
  const [overrideDiscardOpen, setOverrideDiscardOpen] = useState(false);
  useEffect(() => {
    // Pre-dates this change. Surfaced only because lint-staged lints changed
    // files, and `oxlint --max-warnings=0` treats the warning as fatal.
    // oxlint-disable-next-line react/set-state-in-effect
    if (!overrideOpen) setOverrideDirty(false);
  }, [overrideOpen]);

  const wizard = useCreateProgramWizard({
    presetSub,
    onCapped: () => {
      onClose();
      onCapped();
    },
    onCreated,
    createProgram,
    stepUp: showStepUp && createProgram ? createStepUp : undefined,
    resume: showStepUp && createProgram && createStepUp ? resume : null,
    overrides: symbolOverrides,
    onNoRebateAccount: () => {
      onClose();
      onNoRebateAccount?.();
    },
    onLineupReset: () => setSymbolOverrides([]),
  });

  const isDismissable =
    open &&
    wizard.step !== "success" &&
    (!wizard.submitting || wizard.step === "verify");
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [footerEl, setFooterEl] = useState<HTMLDivElement | null>(null);
  const [verifyPhase, setVerifyPhase] = useState<StepUpPhase>("redirect");

  const requestCloseOverride = useCallback(() => {
    if (overrideDirty) {
      setOverrideDiscardOpen(true);
      return;
    }
    setOverrideOpen(false);
  }, [overrideDirty]);

  /**
   * Every *user-initiated* dismissal (X, Esc, backdrop) goes through here so
   * unsaved work gets a confirmation first. Programmatic closes — the cap
   * redirect, a successful create — still call `onClose` directly, because there
   * is nothing left to lose by then.
   */
  const { step: wizardStep, isDirty } = wizard;
  const requestClose = useCallback(() => {
    if (overrideOpen) {
      requestCloseOverride();
      return;
    }
    if (wizardStep === "verify") {
      setDiscardOpen(true);
      return;
    }
    if (isDirty) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  }, [overrideOpen, requestCloseOverride, wizardStep, isDirty, onClose]);

  useEffect(() => {
    if (!isDismissable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // With the confirmation up, Esc answers *it* — dismissing the question is
      // "Stay", not a second request to close.
      if (overrideDiscardOpen) {
        setOverrideDiscardOpen(false);
        return;
      }
      if (discardOpen) {
        setDiscardOpen(false);
        return;
      }
      requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDismissable, discardOpen, overrideDiscardOpen, requestClose]);

  if (!open) return null;

  const totalSteps = wizard.partners.length + (showStepUp ? 3 : 2);
  const currentStepNumber =
    wizard.step === "verify" && verifyPhase === "error"
      ? 0
      : getStepNumber(
          wizard.step,
          wizard.partners.length,
          wizard.relationshipIndex,
        );

  // The partner's name is only shown when the relationship step actually
  // repeats, matching the canvas's `titleName={twoPartner ? … : undefined}`:
  // with a single Sub IB there is no second pass to distinguish it from, so the
  // name would just be noise. Same rule drives the subtitle below.
  const namePartner = wizard.partners.length > 1;
  const title = getStepTitle(
    wizard.step,
    t,
    namePartner ? wizard.currentRelationshipPartner?.displayName : undefined,
  );

  const stepLabel =
    currentStepNumber > 0
      ? t("subIBReport_stepOf", "STEP {{current}} OF {{total}}", {
          current: currentStepNumber,
          total: totalSteps,
        })
      : undefined;

  // Override parties mirror the program's default-split parties. One Sub IB gets
  // the generic "Sub IB" label (there is nothing to disambiguate); two get their
  // display names, each keeping the colour it has on the split step.
  const overrideParties: OverrideParty[] = wizard.partners.map(
    (partner, index) => ({
      subIbId: partner.partnerId,
      label:
        wizard.partners.length === 1
          ? t("subIBReport_defaultSubIb", "Sub IB")
          : partner.displayName,
      colorClass: PARTY_COLOR_CLASSES[index] ?? PARTY_COLOR_CLASSES[0],
    }),
  );

  /**
   * Design change #8's return scroll: after Apply the split step is restored
   * with a summary card appended at its bottom, so land there rather than making
   * the user hunt for what they just configured.
   *
   * Double rAF, not one — the first fires before React has committed the
   * restored step, so `scrollHeight` would still be the sub-page's.
   */
  const applyOverrides = (rules: SymbolOverride[]) => {
    setSymbolOverrides(rules);
    setOverrideOpen(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scroller = modalContentRef.current;
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    });
  };

  const overrideSlot = (
    <OverrideEntry
      rules={symbolOverrides}
      onOpen={() => setOverrideOpen(true)}
    />
  );

  const goToVerify = () => {
    setVerifyPhase("redirect");
    wizard.goToVerify();
  };

  const reviewFooter =
    wizard.step === "review" ? (
      <WizardFooter isMobile={isMobile}>
        <ButtonV2
          variant="tonal"
          size="md"
          onClick={() => wizard.setStep("relationship")}
          disabled={wizard.submitting}
        >
          {t("common_back", "Back")}
        </ButtonV2>
        <ButtonV2
          variant="brand"
          size="md"
          isLoading={
            (!showStepUp || wizard.stepUpControlled) && wizard.submitting
          }
          onClick={
            showStepUp && !wizard.stepUpControlled
              ? goToVerify
              : wizard.doSubmit
          }
          data-testid="create_program_wizard_review_confirm_button"
        >
          {t("subIBReport_confirmCreate", "Confirm")}
        </ButtonV2>
      </WizardFooter>
    ) : null;

  const content = (
    <>
      {overrideOpen ? (
        <RebateSplitOverride
          parties={overrideParties}
          rules={symbolOverrides}
          onApply={applyOverrides}
          onCancel={requestCloseOverride}
          onDirtyChange={setOverrideDirty}
          assets={assets}
          isMobile={isMobile}
          footerContainer={footerEl}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Progress + close stay pinned while the step body scrolls under
              them (design QA). `z-[1]` matches WizardFooter and stays below
              DiscardConfirmOverlay's `z-[2]`; the opaque background is what
              stops content showing through. `pb-6` rather than the previous
              `mb-6` so the background covers that gap instead of leaving a
              transparent strip for content to scroll into, and `pt-5` on
              mobile replaces the vertical padding removed from the shell's
              scroll container below. */}
          <div
            className={clsx(
              "sticky top-0 z-[1] flex items-center gap-10 bg-surface-primary",
              isMobile && "pt-4",
              currentStepNumber > 0 &&
                (wizard.step === "verify" ? !isMobile && "pb-8" : "pb-6"),
            )}
          >
            {currentStepNumber > 0 ? (
              <div
                role="progressbar"
                aria-valuenow={currentStepNumber}
                aria-valuemin={1}
                aria-valuemax={totalSteps}
                aria-label={title}
                className="flex flex-1 gap-1"
              >
                {Array.from({ length: totalSteps }, (_, i) => (
                  <span
                    key={i}
                    className={`h-2 flex-1 rounded-full ${
                      i < currentStepNumber
                        ? "bg-surface-brand-default"
                        : "bg-surface-tonal-bold"
                    }`}
                  />
                ))}
              </div>
            ) : (
              <span className="flex-1" />
            )}
            <button
              type="button"
              aria-label={t("common_close", "Close")}
              data-testid="create_program_wizard_close_button"
              onClick={requestClose}
              disabled={wizard.submitting && wizard.step !== "verify"}
              // Canvas `.wiz-close__icon` is 24px in a 32px box
              // (`.modal__close`), matching ResponsiveModal's built-in header
              // close — so hiding that header must not shrink the glyph.
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CrossIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {wizard.step === "lookup" && (
            <LookupStep
              title={title}
              lookupSubIb={lookupSubIb}
              phase2Enabled={phase2Enabled}
              // Re-seeds the pinned partners when the user comes Back from the
              // first committed step (this step unmounts on every step change).
              initialResults={wizard.gateResults}
              onDirtyChange={wizard.setGateDirty}
              onLineupTouched={wizard.markLineupTouched}
              onContinue={wizard.commitPartners}
              onCancel={requestClose}
              isMobile={isMobile}
              footerContainer={footerEl}
            />
          )}

          {wizard.step === "relationship" &&
            wizard.currentRelationshipPartner && (
              <RelationshipStep
                title={title}
                stepLabel={stepLabel}
                partner={wizard.currentRelationshipPartner}
                onChange={wizard.updateCurrentPartner}
                onBack={wizard.backFromRelationship}
                onContinue={wizard.goToSplit}
                isMobile={isMobile}
                showComment={showRelationshipComment}
                namePartner={namePartner}
                footerContainer={footerEl}
              />
            )}

          {wizard.step === "split" && (
            <SplitStep
              title={title}
              stepLabel={stepLabel}
              partners={wizard.partners}
              split={wizard.split}
              setSplit={wizard.setSplit}
              split2={wizard.split2}
              setSplit2={wizard.setSplit2}
              masterShare={wizard.masterShare}
              onBack={wizard.backFromSplit}
              onContinue={wizard.confirmSplit}
              overrideSlot={overrideSlot}
              isMobile={isMobile}
              footerContainer={footerEl}
            />
          )}

          {wizard.step === "review" && (
            <ReviewStep
              title={title}
              stepLabel={stepLabel}
              partners={wizard.partners}
              masterShare={wizard.masterShare}
              submitError={wizard.submitError}
              isMobile={isMobile}
              showComment={showRelationshipComment}
              masterRebateAccount={masterRebateAccount}
              overrides={symbolOverrides}
              phase2Enabled={phase2Enabled}
            />
          )}

          {wizard.step === "verify" && (
            <VerifyStep
              phase={wizard.stepUpControlled ? wizard.stepUpPhase : undefined}
              onVerified={wizard.doSubmit}
              onBack={wizard.backFromVerify}
              onClose={() => setDiscardOpen(true)}
              onPhaseChange={setVerifyPhase}
              isMobile={isMobile}
              footerContainer={footerEl}
              simulateFailure={simulateStepUpFailure}
              paused={discardOpen}
            />
          )}

          {wizard.step === "success" && wizard.result && (
            <SuccessStep
              title={title}
              result={wizard.result}
              onClose={onClose}
              onViewProgram={() => onViewProgram(wizard.result!.hoidId)}
              isMobile={isMobile}
              footerContainer={footerEl}
            />
          )}
        </div>
      )}
      {discardOpen && (
        <DiscardConfirmOverlay
          onStay={() => setDiscardOpen(false)}
          onDiscard={() => {
            setDiscardOpen(false);
            wizard.cancelSubmit();
            onClose();
          }}
          isMobile={isMobile}
        />
      )}
      {overrideDiscardOpen && (
        <DiscardConfirmOverlay
          onStay={() => setOverrideDiscardOpen(false)}
          onDiscard={() => {
            setOverrideDiscardOpen(false);
            setOverrideOpen(false);
          }}
          isMobile={isMobile}
        />
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-surface-primary">
        <div
          ref={modalContentRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4"
        >
          {content}
        </div>
        <div
          ref={setFooterEl}
          className="shrink-0 bg-surface-primary px-4 pb-4 pt-3"
        >
          {reviewFooter}
        </div>
      </div>
    );
  }

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={requestClose}
      isMobile={false}
      headerClassName="!hidden"
      isDismissable={isDismissable}
      // This wizard owns Escape itself (see the `onKey` effect above) so it
      // can route it through `requestClose`'s dirty-check and answer for its
      // own stacked discard confirmation instead of ResponsiveModal closing
      // straight through underneath it.
      disableEscapeHandling
      contentRef={modalContentRef}
      modalClassName="!max-h-[80vh]"
      // `!` beats ResponsiveModal's hardcoded `mt-6`.
      footerContainerClassName={
        wizard.step === "verify" || wizard.step === "success"
          ? "!mt-8"
          : undefined
      }
      footer={<div ref={setFooterEl}>{reviewFooter}</div>}
    >
      {content}
    </ResponsiveModal>
  );
};
